import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";
import { NotificationCreateInput } from "@bbb/model";
import {
  createMyNotification,
  deleteAllMyNotifications,
  deleteMyNotification,
  followCommunityNotifications,
  getCommunityNotificationFollowStatus,
  listMyNotifications,
  markAllMyNotificationsAsRead,
  markMyNotificationAsRead,
  unfollowCommunityNotifications,
} from "@lib/apiClients";
import { queryKeys } from "@lib/queryClient";
import { useAuthStore } from "@stores/useAuthStore";

export function useNotifications(options?: { enabled?: boolean }) {
  const { authUser, getSessionAuthToken } = useAuthStore(
    useShallow((s) => ({
      authUser: s.authUser,
      getSessionAuthToken: s.getSessionAuthToken,
    }))
  );

  return useQuery({
    queryKey: authUser
      ? queryKeys.notifications(authUser.uid)
      : queryKeys.notifications("missing-user"),
    enabled: (options?.enabled ?? true) && Boolean(authUser),
    queryFn: async ({ signal }) => {
      const token = await getSessionAuthToken();
      return listMyNotifications(token, signal);
    },
  });
}

export function useCommunityNotificationSubscription(
  communityId?: string,
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient();
  const { authUser, getSessionAuthToken } = useAuthStore(
    useShallow((s) => ({
      authUser: s.authUser,
      getSessionAuthToken: s.getSessionAuthToken,
    }))
  );

  const enabled =
    Boolean(authUser?.uid && communityId) && (options?.enabled ?? true);

  const followQuery = useQuery({
    queryKey: queryKeys.communityNotificationFollow(
      authUser?.uid ?? "missing-user",
      communityId ?? "missing-community"
    ),
    enabled,
    queryFn: async () => {
      const token = await getSessionAuthToken();
      return getCommunityNotificationFollowStatus(token, communityId as string);
    },
  });

  const followMutation = useMutation({
    mutationFn: async () => {
      const token = await getSessionAuthToken();
      const currentFollowState = Boolean(followQuery.data?.followed);
      if (currentFollowState) {
        return unfollowCommunityNotifications(token, communityId as string);
      }
      return followCommunityNotifications(token, communityId as string);
    },
    onMutate: async () => {
      if (!authUser?.uid || !communityId) {
        return { previous: undefined as { followed: boolean } | undefined };
      }

      const queryKey = queryKeys.communityNotificationFollow(authUser.uid, communityId);

      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<{ followed: boolean }>(queryKey);
      const nextFollowed = !Boolean(previous?.followed);

      queryClient.setQueryData(queryKey, { followed: nextFollowed });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (!authUser?.uid || !communityId) return;
      const queryKey = queryKeys.communityNotificationFollow(authUser.uid, communityId);

      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: () => {
      if (!authUser?.uid || !communityId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.communityNotificationFollow(authUser.uid, communityId),
      });
    },
  });

  return {
    isFollowed: Boolean(followQuery.data?.followed),
    isLoading: followQuery.isLoading || followMutation.isPending,
    toggle: () => followMutation.mutateAsync(),
  };
}

export function useCreateNotificationMutation() {
  return useMutation({
    mutationFn: async (payload: NotificationCreateInput) => {
      const token = await useAuthStore.getState().getSessionAuthToken();
      return createMyNotification(token, payload);
    },
  });
}

export function useMarkNotificationAsReadMutation() {
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const token = await useAuthStore.getState().getSessionAuthToken();
      return markMyNotificationAsRead(token, notificationId);
    },
  });
}

export function useMarkAllNotificationsAsReadMutation() {
  return useMutation({
    mutationFn: async () => {
      const token = await useAuthStore.getState().getSessionAuthToken();
      return markAllMyNotificationsAsRead(token);
    },
  });
}

export function useDeleteNotificationMutation() {
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const token = await useAuthStore.getState().getSessionAuthToken();
      return deleteMyNotification(token, notificationId);
    },
  });
}

export function useDeleteAllNotificationsMutation() {
  return useMutation({
    mutationFn: async () => {
      const token = await useAuthStore.getState().getSessionAuthToken();
      return deleteAllMyNotifications(token);
    },
  });
}
